import { useCallback, useMemo, useRef, useState } from "react"
import Window from "../Window/Window"
import ImageViewer from "../ImageViewer/ImageViewer"
import GalleryHeader from "./GalleryHeader"
import AlbumsGrid from "./AlbumsGrid"
import PhotosGrid from "./PhotosGrid"
import { useAlbumsData } from "./useAlbumsData"
import { translations } from "../../translations"
import { useTranslationUpdate } from "../../hooks/useTranslationUpdate"
import type { Album } from "./types"
import styles from "./GalleryWindow.module.css"

type Props = { 
  onClose: () => void
  isMinimized: boolean
  onToggleMinimize: () => void 
}

export default function GalleryWindow({ onClose, isMinimized, onToggleMinimize }: Props) {
  useTranslationUpdate()
  const { albums, error } = useAlbumsData()
  const [currentAlbum, setCurrentAlbum] = useState<Album | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerIndex, setViewerIndex] = useState(0)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  const photos = useMemo(() => currentAlbum?.photos ?? [], [currentAlbum])

  const viewerItems = useMemo(() => 
    photos.map(p => ({ src: p.src, label: p.label })),
    [photos]
  )

  const openViewer = useCallback((index: number) => {
    setViewerIndex(index)
    setViewerOpen(true)
  }, [])

  const closeViewer = useCallback(() => {
    setViewerOpen(false)
  }, [])
  
  const openAlbum = useCallback((album: Album) => {
    setCurrentAlbum(album)
    scrollerRef.current?.scrollTo(0, 0)
  }, [])
  
  const backToAlbums = useCallback(() => {
    setCurrentAlbum(null)
    scrollerRef.current?.scrollTo(0, 0)
  }, [])

  if (isMinimized) return null

  const windowProps = {
    title: "Photos",
    onClose,
    isMinimized,
    onToggleMinimize,
    initialPosition: { x: 100, y: 40 },
    initialSize: { width: 1000, height: 680 },
    minSize: { width: 720, height: 480 },
    bodyClassName: styles.body,
    containerClassName: styles.poppinsAll
  }

  if (!currentAlbum) {
    return (
      <Window {...windowProps}>
        <div className={styles.wrap}>
          <GalleryHeader title={translations("gallery.my_albums")} />

          {error && !albums.length && <div className={styles.error}>{error}</div>}

          <div ref={scrollerRef} className={styles.scroller}>
            <AlbumsGrid albums={albums} onAlbumClick={openAlbum} />
          </div>
        </div>
      </Window>
    )
  }

  return (
    <Window {...windowProps}>
      <div className={styles.wrap}>
        <GalleryHeader
          title={translations(currentAlbum.name)}
          onBack={backToAlbums}
        />

        {error && !photos.length && <div className={styles.error}>{error}</div>}

        <div ref={scrollerRef} className={styles.scroller}>
          <PhotosGrid
            photos={photos}
            onPhotoClick={openViewer}
          />
        </div>
      </div>

      {viewerOpen && (
        <ImageViewer
          items={viewerItems}
          index={viewerIndex}
          onClose={closeViewer}
        />
      )}
    </Window>
  )
}
